/**
 * Ürün Kartı — eski `products_single.php` karşılığı.
 *
 * ★ BU EKRANIN EN KRİTİK NOKTASI GİZLİLİKTİR.
 *
 * Maliyet/Kâr bloğu `products/{id}/private/cost` alt dokümanından okunur ve
 * YALNIZCA `financeView` (boss) claim'i varsa hem sorgu yapılır hem gösterilir.
 * Yetki yoksa `<CostPanel>` hiç render EDİLMEZ; dolayısıyla o alt dokümana
 * abonelik de kurulmaz. Rules zaten reddederdi — gereksiz hata üretmiyoruz.
 *
 * Eski sistemin düzeltilen hataları:
 *  · `showmaterialcost` yalnızca ekranı gizliyordu; veri yine istemciye iniyordu.
 *    Artık gizlilik sınırı DOKÜMAN sınırıdır.
 *  · "Fiyat ucuz!!!" uyarısı hesaplanıp hiçbir yere yazılmıyordu (ölü değişken).
 *    Ürün fiyatlandırmanın asıl amacı buydu; artık görünür.
 *  · Parite eşiği `strpos($cats,'Oturma Grubu')` ile metin aramasıyla bulunuyordu.
 *    Artık eşik sunucudan, `settings/finance.parityThresholds` üzerinden gelir.
 *  · `production` rolü ürünü düzenleyebiliyordu (G-11) — artık tüm alanlar
 *    readOnly ve kaydet butonu gizli.
 *  · Fiyat girişinde `,` → `.` dönüşümü yoktu; "12,50" 12 olarak kaydediliyordu.
 */

import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { addDoc, collection, doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { PageHeader, Modal, Spinner, EmptyState, StatusBadge, useToast } from "../components";
import { useDoc, useCallableAction } from "../hooks";
import { db, auth } from "../lib/firebase";
import { useCan } from "../lib/auth";
import { tl, n, pct, dt } from "../lib/format";
import { DecimalInput, ErrorNote, Field, MoneyInput } from "../components";
import { formatDecimalInput, formatKurusInput, parseDecimal, parseKurus } from "../lib/decimal";
import { PRODUCT_STATUS_TR, productStatusTone, type ProductDoc, type ProductStatus } from "./Products";
import { RecipeBlock, LegBlock } from "./Recipe";
import { PackagingBlock } from "./Packaging";
import { StockPanel } from "../components/StockPanel";

/**
 * `products/{id}/private/cost` — YALNIZCA financeView okur.
 * Tüm tutarlar KURUŞ (integer). Hesaplayan tek yer: `recalculateProductCost`.
 */
export interface ProductCost {
  materialKurus: number;
  cargoKurus: number;
  totalCostKurus: number;

  /**
   * ★ "SIFIR" İLE "ÖLÇÜLMEDİ" AYRIMI — sunucu (costing/compute.ts) yazar.
   *
   * Reçetesi olmayan ürünün malzemesi 0, kolisi olmayan ürünün kargosu 0
   * hesaplanır. İkisi de gerçek maliyet DEĞİL, ölçülmemiş maliyettir. Bu
   * bayraklar olmadan ekranda ₺0,00 ile "bedava" birbirinden ayrılamıyordu.
   */
  materialKnown?: boolean;
  cargoKnown?: boolean;
  costKnown?: boolean;
  costBasis?: "tam" | "yalnizca-malzeme" | "yalnizca-kargo" | "olculmedi";
  /** Kâr rakamı üst sınır mı? (maliyetin bir parçası ölçülmedi) */
  profitIsUpperBound?: boolean;

  netRevenueKurus: number | null;
  commissionKurus: number | null;
  grossProfitKurus: number | null;
  /** Brüt kâr / net hasılat, yüzde. Maliyet hiç ölçülmediyse `null`. */
  profitPercent: number | null;
  /** Satış fiyatı / maliyet. Eski sistemin "parite" kavramı. */
  parity: number | null;
  /** Kargo maliyetinin dayandığı toplam desi. */
  desi: number;
  /** Ürün kategorisine göre seçilmiş eşik (Oturma Grubu 3,5 · Karyola 3 · diğer 4). */
  parityThreshold: number;
  isUnderpriced: boolean;
  isIncomplete: boolean;
  warnings: string[];

  /**
   * ★ ALAN ADI `updatedAt` — `computedAt` DEĞİL.
   * Burada `computedAt` okunuyordu; sunucu o adı hiç yazmıyor (canlı veride 516
   * maliyet dokümanının hiçbirinde yok). "Son hesaplama" satırı bu yüzden HER
   * ÜRÜNDE "—" gösteriyordu ve rakamın ne kadar eski olduğu görünmüyordu.
   */
  updatedAt?: { toDate: () => Date } | null;
  computedAt?: { toDate: () => Date } | null;

  /**
   * ★ ALAN ADI `settingsUsed` — `settings` DEĞİL.
   * Sunucu `settingsUsed` yazıyor (costing/service.ts). Burada `settings`
   * okunduğu için "Kullanılan parametreler" satırı HİÇ GÖRÜNMÜYORDU: rakamın
   * hangi KDV/komisyon ile hesaplandığı denetlenemiyordu.
   */
  settingsUsed?: FinanceParams;
  settings?: FinanceParams;
}

interface FinanceParams {
  kdv: number;
  komisyon: number;
  shopifyKomisyon: number;
  /** ★ TL cinsindendir (kuruş DEĞİL) — sistemdeki tek istisna. `n()` ile basılır. */
  kargodesifiyat: number;
}

const INPUT =
  "w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-sky-500 focus:outline-none " +
  "focus:ring-1 focus:ring-sky-500 read-only:bg-slate-100 read-only:text-slate-600 disabled:bg-slate-100";

const NEW = "new";   // rota: /products/new

type Tab = "bilgi" | "recete" | "paket";

export default function ProductDetail() {
  // Rota parametresi `:productId` (App.tsx). Önceden `id` okunuyordu ve her
  // zaman undefined geliyordu → doc(db,"products",undefined) "tek segment"
  // hatasıyla ekranı çökertiyordu.
  const { productId: routeId } = useParams<{ productId: string }>();
  const productId = routeId ?? "";

  // Yeni ürün ve mevcut ürün AYRI bileşenlerdir: böylece "yeni" modunda
  // var olmayan bir dokümana abonelik hiç kurulmaz.
  return productId === NEW ? <NewProduct /> : <ExistingProduct productId={productId} />;
}

function NewProduct() {
  const can = useCan();
  return (
    <PageHeader title="Yeni Ürün">
      {can.isStaff ? (
        <ProductForm product={null} />
      ) : (
        <EmptyState
          title="Yetkiniz yok"
          description="Üretim rolünde ürün eklenemez. Ürün kartlarını yalnızca görüntüleyebilirsiniz."
        />
      )}
    </PageHeader>
  );
}

function ExistingProduct({ productId }: { productId: string }) {
  const { data: product, loading, error } = useDoc<ProductDoc>(`products/${productId}`);
  const [tab, setTab] = useState<Tab>("bilgi");
  const can = useCan();

  return (
    <PageHeader
      title={product ? product.name : "Ürün Kartı"}
    >
      {error ? <ErrorNote error={error} /> : null}
      {loading ? <Spinner /> : null}
      {!loading && !product ? (
        <EmptyState title="Ürün bulunamadı" description="Bağlantı geçersiz veya ürün silinmiş." />
      ) : null}

      {product ? (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <StatusBadge
              label={PRODUCT_STATUS_TR[product.status ?? "DRAFT"]}
              color={productStatusTone(product.status)}
            />
            <span className="text-sm text-slate-500">SKU: {product.sku || "—"}</span>
            <span className="text-sm text-slate-500">
              Satış Fiyatı: <strong className="tabular-nums text-slate-800">{tl(product.price)}</strong>
            </span>
            <Link to="/products" className="ml-auto text-sm text-sky-700 hover:underline">
              ← Ürün listesi
            </Link>
          </div>

          {/*
            MALİYET / KÂR BLOĞU — yalnızca financeView.
            Koşul render seviyesindedir: yetki yoksa bileşen mount edilmez,
            dolayısıyla `private/cost` aboneliği hiç açılmaz.
          */}
          {can.seeFinance ? (
            <CostPanel productId={productId} priceKurus={product.price ?? null} />
          ) : (
            <p className="mb-6 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
              Maliyet ve kâr bilgisi bu hesapta görüntülenemez. Sipariş tutarı ve ciro tüm rollere
              açıktır; gizli olan yalnızca maliyet ve kârdır.
            </p>
          )}

          <nav className="mb-4 flex gap-1 border-b border-slate-200 text-sm">
            {(
              [
                ["bilgi", "Ürün Bilgileri"],
                ["recete", "Reçete ve Ayak"],
                ["paket", "Paket Bilgileri"],
              ] as Array<[Tab, string]>
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={
                  "-mb-px border-b-2 px-4 py-2 " +
                  (tab === key
                    ? "border-sky-600 font-medium text-sky-700"
                    : "border-transparent text-slate-500 hover:text-slate-800")
                }
              >
                {label}
              </button>
            ))}
          </nav>

          {tab === "bilgi" ? (
            <div className="space-y-6">
              {/* Stok ve teslim süresi — "hızlı gönderim" ayrımı burada tutulur. */}
              <StockPanel productId={productId} product={product} />
              <ProductForm product={product} />
            </div>
          ) : null}
          {tab === "recete" ? (
            <div className="space-y-8">
              <RecipeBlock productId={productId} />
              <LegBlock productId={productId} />
            </div>
          ) : null}
          {tab === "paket" ? <PackagingBlock productId={productId} /> : null}
        </>
      ) : null}
    </PageHeader>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MALİYET / KÂR — financeView
// ═══════════════════════════════════════════════════════════════════════════

function CostPanel({ productId, priceKurus }: { productId: string; priceKurus: number | null }) {
  const { data: cost, loading, error } = useDoc<ProductCost>(`products/${productId}/private/cost`);
  /**
   * ★ `recalculateProductCost` diye bir fonksiyon YOK — bu yüzden "Yeniden
   * Hesapla" her tıkta `internal` hatası veriyordu. Sunucudaki karşılığı
   * `getProductCost`; `force: true` önbelleği atlayıp yeniden hesaplar.
   */
  const recalc = useCallableAction<{ productId: string; force: boolean }, { totalCostKurus: number }>(
    "getProductCost",
  );
  const toast = useToast();

  return (
    <section className="mb-6 rounded border border-emerald-300 bg-emerald-50/60 p-4">
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-emerald-900">Maliyet ve Kâr</h3>
          <p className="text-xs text-emerald-800">
            Yalnızca iş sahibine açıktır. Rakamlar sunucuda tek formülle hesaplanır.
          </p>
        </div>
        <button
          type="button"
          disabled={recalc.loading}
          onClick={async () => {
            const res = await recalc.run({ productId, force: true });
            if (res) toast.success("Maliyet yeniden hesaplandı.");
          }}
          className="rounded border border-emerald-600 bg-white px-3 py-1.5 text-xs text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
        >
          {recalc.loading ? "Hesaplanıyor…" : "Yeniden Hesapla"}
        </button>
      </header>

      <ErrorNote error={error ?? recalc.error} />
      {loading ? <Spinner /> : null}

      {!loading && !cost ? (
        <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Bu ürünün maliyeti hiç hesaplanmamış. "Yeniden Hesapla" ile başlatabilirsiniz.
        </p>
      ) : null}

      {cost ? (
        <>
          {/* Eski sistemde "Fiyat ucuz!!!" hesaplanıp ekrana hiç yazılmıyordu. */}
          {cost.isUnderpriced ? (
            <div className="mb-3 rounded border border-red-400 bg-red-100 px-3 py-2 text-sm font-semibold text-red-900">
              Fiyat ucuz! Parite {n(cost.parity)} — bu kategori için beklenen en az{" "}
              {n(cost.parityThreshold)}.
            </div>
          ) : null}

          {/*
            ★ "Eksik veri" uyarısı artık NEYİN eksik olduğunu söyler ve kâr
            rakamının ne anlama geldiğini açıkça yazar. Eskiden tek bir genel
            cümle vardı; kullanıcı %89'un uydurma mı gerçek mi olduğunu
            anlayamıyordu.
          */}
          {cost.costBasis === "olculmedi" ? (
            <div className="mb-3 rounded border border-slate-400 bg-slate-100 px-3 py-2 text-sm text-slate-800">
              Bu ürünün <strong>maliyeti hiç ölçülmedi</strong> (reçete ve koli bilgisi yok).
              Kâr oranı ve parite bilerek <strong>boş bırakıldı</strong> — sıfır maliyetle
              hesaplansaydı %89 gibi uydurma bir oran çıkardı.
            </div>
          ) : cost.profitIsUpperBound ? (
            <div className="mb-3 rounded border border-amber-400 bg-amber-100 px-3 py-2 text-sm text-amber-900">
              Maliyetin yalnızca{" "}
              <strong>{cost.materialKnown ? "malzeme" : "kargo"}</strong> tarafı ölçüldü.
              Aşağıdaki kâr rakamı <strong>ÜST SINIRDIR</strong>; gerçek kâr bundan düşüktür.
            </div>
          ) : cost.isIncomplete ? (
            <div className="mb-3 rounded border border-amber-400 bg-amber-100 px-3 py-2 text-sm text-amber-900">
              Maliyet <strong>eksik veriyle</strong> hesaplandı; kâr rakamı yanıltıcı olabilir.
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {/*
              ★ ÖLÇÜLMEYEN MALİYET ₺0,00 OLARAK GÖSTERİLMEZ.
              `materialKnown === false` iken ₺0,00 yazmak, "bu ürünün malzemesi
              bedava" demekle aynı ekran çıktısını veriyordu.
            */}
            <Metric
              label="Malzeme Maliyeti"
              value={cost.materialKnown === false ? "ölçülmedi" : tl(cost.materialKurus)}
              hint={cost.materialKnown === false ? "reçete eksik — sıfır değil" : undefined}
              tone={cost.materialKnown === false ? "muted" : undefined}
            />
            <Metric
              label="Kargo Maliyeti"
              value={cost.cargoKnown === false ? "ölçülmedi" : tl(cost.cargoKurus)}
              hint={
                cost.cargoKnown === false
                  ? "ölçülü koli yok — sıfır değil"
                  : `${n(cost.desi)} desi`
              }
              tone={cost.cargoKnown === false ? "muted" : undefined}
            />
            <Metric
              label="Toplam Maliyet"
              value={cost.costBasis === "olculmedi" ? "ölçülmedi" : tl(cost.totalCostKurus)}
              hint={
                cost.costBasis === "yalnizca-malzeme"
                  ? "yalnızca malzeme — kargo hariç"
                  : cost.costBasis === "yalnizca-kargo"
                    ? "yalnızca kargo — malzeme hariç"
                    : undefined
              }
              strong
              tone={cost.costBasis === "olculmedi" ? "muted" : undefined}
            />
            <Metric label="Satış Fiyatı" value={tl(priceKurus)} hint="KDV dâhil vitrin fiyatı" />

            <Metric label="Net Hasılat" value={tl(cost.netRevenueKurus)} hint="KDV arındırılmış" />
            <Metric label="Kesintiler" value={tl(cost.commissionKurus)} hint="komisyon + Shopify" />
            <Metric
              label={cost.profitIsUpperBound ? "Brüt Kâr (üst sınır)" : "Brüt Kâr"}
              value={tl(cost.grossProfitKurus)}
              hint="net hasılat − kesintiler − maliyet"
              strong
              tone={
                cost.grossProfitKurus !== null && cost.grossProfitKurus < 0 ? "bad" : "good"
              }
            />
            <Metric
              label={cost.profitIsUpperBound ? "Kâr Oranı (üst sınır)" : "Kâr Oranı"}
              value={pct(cost.profitPercent)}
              /* ★ Oranın HANGİ TABANDAN hesaplandığı ekranda yazar. */
              hint={
                cost.profitPercent === null
                  ? "maliyet ölçülmediği için hesaplanmadı"
                  : `brüt kâr / net hasılat · parite ${n(cost.parity)} / eşik ${n(
                      cost.parityThreshold,
                    )}`
              }
              tone={cost.isUnderpriced ? "bad" : "good"}
            />
          </div>

          {/* ★ `settingsUsed` sunucunun yazdığı ad; `settings` yalnızca geriye dönük. */}
          {(() => {
            const s = cost.settingsUsed ?? cost.settings;
            return s ? (
              <p className="mt-3 text-xs text-emerald-800">
                Kullanılan parametreler: KDV %{n(s.kdv)} · Komisyon %{n(s.komisyon)} · Shopify %
                {n(s.shopifyKomisyon)} · Kargo {n(s.kargodesifiyat)} TL/desi
              </p>
            ) : (
              <p className="mt-3 text-xs text-amber-800">
                Bu maliyet hangi parametrelerle hesaplandığı kaydedilmeden yazılmış —
                "Yeniden Hesapla" ile tazeleyin.
              </p>
            );
          })()}

          {/* ★ `updatedAt` sunucunun yazdığı ad; `computedAt` hiç yazılmıyor. */}
          <p className="mt-1 text-xs text-emerald-700">
            Son hesaplama: {dt(cost.updatedAt ?? cost.computedAt ?? null)}
          </p>

          {/* Sessiz atlama yok: eksik fiyat/reçete/desi uyarıları aynen listelenir. */}
          {cost.warnings.length > 0 ? (
            <ul className="mt-3 space-y-1 rounded border border-amber-300 bg-white px-3 py-2 text-xs text-amber-900">
              {cost.warnings.map((w) => (
                <li key={w}>• {w}</li>
              ))}
            </ul>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

function Metric({
  label,
  value,
  hint,
  strong,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  strong?: boolean;
  /** `muted` = ÖLÇÜLMEDİ; gerçek bir rakam gibi görünmemesi için soluk basılır. */
  tone?: "good" | "bad" | "muted";
}) {
  const color =
    tone === "bad"
      ? "text-red-700"
      : tone === "muted"
        ? "text-slate-400 italic"
        : tone === "good"
          ? "text-emerald-900"
          : "text-slate-800";
  return (
    <div className="rounded border border-emerald-200 bg-white px-3 py-2">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`tabular-nums ${strong ? "text-lg font-semibold" : "text-base"} ${color}`}>
        {value}
      </div>
      {hint ? <div className="text-xs text-slate-600">{hint}</div> : null}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ÜRÜN BİLGİLERİ FORMU
//
// Ürün dokümanı Rules tarafından alan beyaz listesiyle korunur; maliyet/kâr
// alanları (cost, grossProfit, profitPercent, materialCost) ürün dokümanına
// yazılamaz — gizlilik sınırı doküman sınırıdır. Bu yüzden burada da yalnızca
// satış tarafı alanlar düzenlenir.
// ═══════════════════════════════════════════════════════════════════════════

function ProductForm({ product }: { product: ProductDoc | null }) {
  const can = useCan();
  const toast = useToast();
  const navigate = useNavigate();
  const readOnly = !can.isStaff;

  const [name, setName] = useState(product?.name ?? "");
  const [sku, setSku] = useState(product?.sku ?? "");
  const [priceText, setPriceText] = useState(formatKurusInput(product?.price));
  const [status, setStatus] = useState<ProductStatus>(product?.status ?? "DRAFT");
  const [barcode, setBarcode] = useState(product?.barcode ?? "");
  const [weightNet, setWeightNet] = useState(formatDecimalInput(product?.weightNet));
  const [weightPacked, setWeightPacked] = useState(formatDecimalInput(product?.weightPacked));
  const [productionNote, setProductionNote] = useState(product?.productionNote ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmArchive, setConfirmArchive] = useState(false);

  // Doküman canlı aboneliktir; başka bir kullanıcı kaydettiğinde formu tazele.
  useEffect(() => {
    if (!product) return;
    setName(product.name);
    setSku(product.sku ?? "");
    setPriceText(formatKurusInput(product.price));
    setStatus(product.status ?? "DRAFT");
    setBarcode(product.barcode ?? "");
    setWeightNet(formatDecimalInput(product.weightNet));
    setWeightPacked(formatDecimalInput(product.weightPacked));
    setProductionNote(product.productionNote ?? "");
  }, [product]);

  async function save() {
    setErr(null);

    if (!name.trim()) {
      setErr("Ürün adı zorunludur.");
      return;
    }

    let price: number | null = null;
    if (priceText.trim()) {
      price = parseKurus(priceText);
      if (price === null) {
        setErr('Satış fiyatı okunamadı. Ondalık için virgül kullanın (örnek: 12.499,90).');
        return;
      }
      if (price < 0) {
        setErr("Satış fiyatı negatif olamaz.");
        return;
      }
    }

    const uid = auth.currentUser?.uid;
    if (!uid) {
      setErr("Oturum bulunamadı. Lütfen yeniden giriş yapın.");
      return;
    }

    const payload = {
      name: name.trim(),
      sku: sku.trim() || null,
      price,
      status,
      // ★ İKİSİ BİRLİKTE yazılır: Kârlılık Tablosu `isActive`e bakar, ekran
      // `status`a. Yalnızca `status` yazılınca ürün "Yayında" görünüp rapordan
      // sessizce düşüyordu.
      isActive: status === "ACTIVE",
      barcode: barcode.trim() || null,
      weightNet: parseDecimal(weightNet),
      weightPacked: parseDecimal(weightPacked),
      productionNote: productionNote.trim() || null,
      updatedAt: serverTimestamp(),
      updatedBy: uid,
    };

    setBusy(true);
    try {
      if (product) {
        await updateDoc(doc(db, "products", product.id), payload);
        toast.success("Ürün kaydedildi.");
      } else {
        const ref = await addDoc(collection(db, "products"), {
          ...payload,
          createdAt: serverTimestamp(),
          createdBy: uid,
          deleted: false,
        });
        toast.success("Ürün oluşturuldu.");
        navigate(`/products/${ref.id}`);
      }
    } catch (e) {
      // Sessiz başarı yok: sunucudan gelen mesaj olduğu gibi gösterilir.
      setErr(e instanceof Error ? e.message : "Kayıt sırasında bilinmeyen bir hata oluştu.");
    } finally {
      setBusy(false);
    }
  }

  async function softDelete() {
    const uid = auth.currentUser?.uid;
    if (!product || !uid) return;
    setBusy(true);
    setErr(null);
    try {
      // Silme yok — soft delete. Reçete ve stok referansları yetim kalmasın diye.
      await updateDoc(doc(db, "products", product.id), {
        deleted: true,
        deletedAt: serverTimestamp(),
        deletedBy: uid,
        updatedAt: serverTimestamp(),
        updatedBy: uid,
      });
      toast.success("Ürün listeden kaldırıldı.");
      navigate("/products");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Kaldırma sırasında bilinmeyen bir hata oluştu.");
    } finally {
      setBusy(false);
      setConfirmArchive(false);
    }
  }

  return (
    <section>
      {readOnly ? (
        <p className="mb-4 rounded border border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Üretim rolünde ürün kartı <strong>salt okunurdur</strong>. Reçete ve paket bilgilerini
          görebilir, değiştiremezsiniz.
        </p>
      ) : null}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="md:col-span-2">
          <Field label="Ürün Adı">
            <input
              className={INPUT}
              value={name}
              readOnly={readOnly}
              maxLength={300}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
        </div>

        <Field label="SKU">
          <input
            className={INPUT}
            value={sku}
            readOnly={readOnly}
            maxLength={100}
            onChange={(e) => setSku(e.target.value)}
          />
        </Field>

        <Field label="Satış Fiyatı" hint="KDV dâhil vitrin fiyatı. Ondalık için virgül: 12.499,90">
          <MoneyInput value={priceText} onChange={setPriceText} readOnly={readOnly} />
        </Field>

        {/*
          ★ SHOPIFY'DAN GELEN DURUM BURADAN DEĞİŞTİRİLMEZ.
          Gece taraması (`shopify/priceSync.ts`) Shopify'da karşılığı olan her
          ürünün durumunu Shopify'dan yazar ve `statusSyncedAt` damgası bırakır.
          Burada değiştirilen durum ertesi gece SESSİZCE geri alınırdı — hızlı
          gönderim ayarı aynı sebeple StockPanel'den kaldırılmıştı. Shopify'da
          karşılığı olmayan ürünlerde seçim açık kalır.
        */}
        <Field
          label="Statü"
          hint={
            product?.statusSyncedAt
              ? "Shopify'dan gelir; değiştirmek için ürünün durumunu Shopify'da değiştirin. Balder her gece 03:30'da ya da Ürünler → \"Shopify'dan güncelle\" ile tazelenir."
              : undefined
          }
        >
          <select
            className={INPUT}
            value={status}
            disabled={readOnly || Boolean(product?.statusSyncedAt)}
            onChange={(e) => setStatus(e.target.value as ProductStatus)}
          >
            {(Object.keys(PRODUCT_STATUS_TR) as ProductStatus[]).map((s) => (
              <option key={s} value={s}>
                {PRODUCT_STATUS_TR[s]}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Barkod">
          <input
            className={INPUT}
            value={barcode}
            readOnly={readOnly}
            maxLength={64}
            onChange={(e) => setBarcode(e.target.value)}
          />
        </Field>

        <Field label="Ürün Ağırlığı (kg)" hint="Net ağırlık">
          <DecimalInput value={weightNet} onChange={setWeightNet} readOnly={readOnly} />
        </Field>

        <Field label="Koli Ağırlığı (kg)" hint="Paketli ağırlık">
          <DecimalInput value={weightPacked} onChange={setWeightPacked} readOnly={readOnly} />
        </Field>

        <div className="md:col-span-2">
          <Field label="Üretim Notu">
            <textarea
              className={INPUT}
              rows={3}
              value={productionNote}
              readOnly={readOnly}
              maxLength={2000}
              onChange={(e) => setProductionNote(e.target.value)}
            />
          </Field>
        </div>
      </div>

      <ErrorNote error={err} />

      {!readOnly ? (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
          >
            {busy ? "Kaydediliyor…" : product ? "Kaydet" : "Ürünü Oluştur"}
          </button>
          {product ? (
            <button
              type="button"
              onClick={() => setConfirmArchive(true)}
              className="rounded border border-red-300 px-4 py-2 text-sm text-red-700 hover:bg-red-50"
            >
              Listeden Kaldır
            </button>
          ) : null}
        </div>
      ) : null}

      {confirmArchive && product ? (
        <Modal open onClose={() => setConfirmArchive(false)} title="Ürünü listeden kaldır">
          <p className="text-sm text-slate-700">
            <strong>{product.name}</strong> listelerden kaldırılacak. Kayıt silinmez; reçete, stok
            ve geçmiş sipariş bağlantıları korunur. Geri almayı yalnızca iş sahibi yapabilir.
          </p>
          <ErrorNote error={err} />
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              className="rounded border border-slate-300 px-4 py-2 text-sm"
              onClick={() => setConfirmArchive(false)}
            >
              Vazgeç
            </button>
            <button
              type="button"
              disabled={busy}
              className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              onClick={() => void softDelete()}
            >
              {busy ? "Kaldırılıyor…" : "Kaldır"}
            </button>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
